import React, { useEffect, useState } from 'react'
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react'

export type SnackbarType = 'success' | 'error' | 'warning' | 'info'

interface SnackbarProps {
  message: string
  type: SnackbarType
  onClose: () => void
  duration?: number
}

export function Snackbar({ message, type, onClose, duration = 5000 }: SnackbarProps) {
  const [isExiting, setIsExiting] = useState(false)

  const handleClose = () => {
    setIsExiting(true)
    setTimeout(() => {
      onClose()
    }, 300) // Wait for exit animation
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      handleClose()
    }, duration)

    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duration])

  const getStyles = () => {
    switch (type) {
      case 'success':
        return 'bg-green-600 text-white border-green-700'
      case 'error':
        return 'bg-red-600 text-white border-red-700'
      case 'warning':
        return 'bg-amber-600 text-white border-amber-700'
      case 'info':
        return 'bg-blue-600 text-white border-blue-700'
      default:
        return 'bg-gray-600 text-white border-gray-700'
    }
  }

  const getIcon = () => {
    const iconClass = "h-5 w-5 flex-shrink-0"
    switch (type) {
      case 'success':
        return <CheckCircle className={iconClass} />
      case 'error':
        return <XCircle className={iconClass} />
      case 'warning':
        return <AlertTriangle className={iconClass} />
      case 'info':
        return <Info className={iconClass} />
      default:
        return null
    }
  }

  return (
    <div
      className={`
        flex items-start gap-3 px-4 py-3 rounded-lg border shadow-lg min-w-[320px] max-w-md
        ${getStyles()}
        ${isExiting ? 'animate-slide-out-right' : 'animate-slide-in-right'}
      `}
    >
      <div className="flex-shrink-0 mt-0.5">
        {getIcon()}
      </div>
      <div className="flex-1 text-sm font-medium leading-relaxed">
        {message}
      </div>
      <button
        onClick={handleClose}
        className="flex-shrink-0 ml-2 inline-flex text-white/80 hover:text-white focus:outline-none transition-colors"
        aria-label="Close notification"
      >
        <X className="h-5 w-5" />
      </button>
    </div>
  )
}
